import { useEffect, useRef } from 'react';
import { supabase } from '../supabase';

/**
 * Subscribe to Supabase Realtime postgres_changes for a table scoped to a
 * workspace. Calls `onSync` (typically a full re-fetch) whenever any row in
 * that table+workspace is inserted, updated, or deleted by any client.
 *
 * Rapid events are debounced so a burst of writes (e.g. a multi-turn
 * simulation run) collapses into a single re-fetch.
 */
export function useRealtimeSync(
  table: string,
  workspaceId: string | undefined,
  onSync: () => void,
  debounceMs = 1000,
) {
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;

  useEffect(() => {
    if (!workspaceId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const debouncedSync = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => onSyncRef.current(), debounceMs);
    };

    const channel = supabase
      .channel(`realtime:${table}:${workspaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
          filter: `workspace_id=eq.${workspaceId}`,
        },
        debouncedSync,
      )
      .subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [table, workspaceId, debounceMs]);
}
